/**
 * Шкала дня автоакцепта — одна на вкладку «Автоакцепт» (миниатюра `sm` в раскрытой строке сотрудника) и
 * на страницу смены (`lg`: дорожки статусов, акцепта CRM и автоакцепта на общей оси). Спека
 * `docs/specs/2026-11-05-activity-autoaccept-design.md`, «Экран 1» и «Экран 2».
 */
import type { ReactNode } from "react";

import { type AcceptStatus, type BarSegment, reasonText, type Span } from "@/lib/accept";
import { workTime } from "@/lib/leads";

/** Цвета статусов — как на смене КЦ (`ShiftPage`). */
const STATUS_COLOR: Record<number, string> = {
  1: "bg-ok",
  2: "bg-warn",
  3: "bg-err",
  4: "bg-fg-faint",
  5: "bg-accent",
};

/** «ЧЧ:ММ:СС» или ISO → минуты суток. */
export function minuteOfDay(s: string): number {
  const t = s.length > 8 ? s.slice(11, 19) : s;
  return Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5)) + Number(t.slice(6, 8) || 0) / 60;
}
const hm = (s: string) => (s.length > 8 ? s.slice(11, 16) : s.slice(0, 5));

/** Автоакцепт — зелёный, окно простоя — оранжевый, день целиком HR — красный, сдельная оплата — серый. */
export function segmentColor(s: Pick<BarSegment, "kind" | "reason">): string {
  if (s.kind === "auto") return "bg-ok";
  if (s.reason === "idle" || s.reason === "idle_commented") return "bg-warn";
  if (s.reason === "piece" || s.reason === "open") return "bg-fg-faint";
  return "bg-err";
}

export type BarItem = BarSegment & { minutes?: number; hint?: string | null; in_crm?: boolean };

function segmentTitle(s: BarItem): string {
  const minutes = s.minutes ?? minuteOfDay(s.to) - minuteOfDay(s.from);
  const what =
    s.kind === "auto"
      ? `автоакцепт${s.in_crm === undefined ? "" : s.in_crm ? ", в CRM уже стоит" : ", поставит Робот"}`
      : `требует решения: ${reasonText({ reason: s.reason, minutes, hint: s.hint ?? null })}`;
  return `${hm(s.from)}–${hm(s.to)} · ${workTime(minutes)} · ${what}`;
}

type Range = [number, number];

/** Отрезки `base` без отрезков `cut` — «в статусе, но не принято». */
function minus(base: Range[], cut: Range[]): Range[] {
  const out: Range[] = [];
  const cuts = [...cut].sort((x, y) => x[0] - y[0]);
  for (const [a0, b] of base) {
    let a = a0;
    for (const [ca, cb] of cuts) {
      if (cb <= a || ca >= b) continue;
      if (ca > a) out.push([a, ca]);
      a = Math.max(a, cb);
    }
    if (b - a > 0.5) out.push([a, b]);
  }
  return out;
}

export function ShiftBar({
  segments,
  statuses,
  accepted,
  size,
  from = "08:00",
  to = "22:00",
  totals,
}: {
  segments: BarItem[];
  statuses?: AcceptStatus[];
  accepted?: Span[];
  size: "sm" | "lg";
  from?: string;
  to?: string;
  /** Итоги дорожек `lg`: слева от каждой. */
  totals?: { statuses: string; accepted: string; auto: string };
}) {
  // Ось — 08:00–22:00, раздвигается до целого часа, если смена вышла за неё.
  const all = [...segments, ...(statuses ?? []), ...(accepted ?? [])];
  const a = Math.min(minuteOfDay(from), ...all.map((x) => Math.floor(minuteOfDay(x.from) / 60) * 60));
  const b = Math.max(minuteOfDay(to), ...all.map((x) => Math.ceil(minuteOfDay(x.to) / 60) * 60));
  const span = b - a;
  const pos = (x: number, y: number) => ({
    left: `${((x - a) / span) * 100}%`,
    width: `max(2px, ${((y - x) / span) * 100}%)`,
  });
  const at = (s: { from: string; to: string }) => pos(minuteOfDay(s.from), minuteOfDay(s.to));

  const autoBars = segments.map((s) => (
    <div
      key={`${s.from}-${s.kind}`}
      className={`absolute inset-y-0 ${segmentColor(s)}`}
      style={at(s)}
      title={segmentTitle(s)}
    />
  ));
  const acceptedBars = (accepted ?? []).map((r) => (
    <div
      key={`a${r.from}`}
      className="absolute inset-y-0 bg-ok"
      style={at(r)}
      title={`принято в CRM ${hm(r.from)}–${hm(r.to)} · ${workTime(minuteOfDay(r.to) - minuteOfDay(r.from))}`}
    />
  ));

  if (size === "sm")
    return (
      <div className="flex w-[220px] flex-col gap-[2px]" data-annot="миниатюра шкалы дня">
        <div className="relative h-2 overflow-hidden rounded-[2px] bg-bg">{autoBars}</div>
        <div className="relative h-[2px] overflow-hidden">{acceptedBars}</div>
      </div>
    );

  const paid: Range[] = (statuses ?? []).filter((s) => s.paid).map((s) => [minuteOfDay(s.from), minuteOfDay(s.to)]);
  const acceptedRanges: Range[] = (accepted ?? []).map((r) => [minuteOfDay(r.from), minuteOfDay(r.to)]);
  // «Не принято» — только когда акцепт за день уже стоит: иначе весь день «не принят» лишь потому, что до него не дошли.
  const notAccepted = acceptedRanges.length ? minus(paid, acceptedRanges) : [];
  const ticks: number[] = [];
  for (let h = Math.ceil(a / 60); h * 60 <= b; h += 1) ticks.push(h);

  const lanes: { name: string; total: string | undefined; hint: string; bars: ReactNode }[] = [
    {
      name: "Статусы",
      total: totals?.statuses,
      hint: "какой статус стоял: зелёный — «Работаю», жёлтый — «Перерыв», красный — «Ушел», серый и синий — «Задача». Итог — время в оплачиваемых статусах",
      bars: (statuses ?? []).map((st) => (
        <div
          key={st.from}
          className={`absolute inset-y-0 ${STATUS_COLOR[st.status_id] ?? "bg-fg-faint"} opacity-70`}
          style={at(st)}
          title={`${st.name} ${hm(st.from)}–${hm(st.to)} · ${workTime(st.minutes)}`}
        />
      )),
    },
    {
      name: "Акцепт CRM",
      total: totals?.accepted,
      hint: "зелёный — время принято в CRM, за него платят; бледно-красный — был в оплачиваемом статусе, но не принято. Итог — сколько принято и кем",
      bars: [
        ...notAccepted.map(([x, y]) => (
          <div
            key={`n${x}`}
            className="absolute inset-y-0 bg-err-bg"
            style={pos(x, y)}
            title={`не принято · ${workTime(y - x)} — в оплачиваемом статусе`}
          />
        )),
        ...acceptedBars,
      ],
    },
    {
      name: "Автоакцепт",
      total: totals?.auto,
      hint: "зелёный — пройдёт автоакцепт по правилу группы; оранжевый — окно простоя, решает HR; красный — день целиком HR (нет статусов, нет «Ушел», «Ушел» от CRM); серый — сдельная оплата или день идёт. Итог — сколько пройдёт",
      bars: autoBars,
    },
  ];

  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4" data-annot="шкала дня автоакцепта">
      <div className="grid grid-cols-[150px_1fr] gap-x-3 gap-y-2 text-[11.5px]">
        <div />
        <div className="relative h-4 text-fg-faint">
          {ticks.map((h) => (
            <span key={h} className="absolute -translate-x-1/2 tabular-nums" style={{ left: `${((h * 60 - a) / span) * 100}%` }}>
              {h}
            </span>
          ))}
        </div>
        {lanes.map((l) => (
          <div key={l.name} className="contents">
            <div className="flex cursor-help items-baseline justify-between gap-2" title={l.hint} data-annot={`дорожка «${l.name}»`}>
              <span className="text-fg-muted">{l.name}</span>
              {l.total && <span className="truncate tabular-nums text-fg">{l.total}</span>}
            </div>
            <div className="relative h-5 overflow-hidden rounded-[3px] bg-bg">{l.bars}</div>
          </div>
        ))}
      </div>
      <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-1 text-[12px]" data-annot="как читать шкалу">
        {lanes.map((l) => (
          <div key={l.name} className="flex gap-2">
            <dt className="w-[90px] shrink-0 font-medium text-fg-muted">{l.name}</dt>
            <dd className="text-fg-faint">{l.hint}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-2 text-[12px] text-fg-faint">Наведите на полосу — там время, минуты и причина.</div>
    </section>
  );
}
